import { Link } from "react-router-dom";
import { StatusBadge } from "@/components/design";
import { PayPeriodBanner } from "@/features/dashboard/components/pay-period-banner";
import { CrewStatusRing } from "@/features/dashboard/components/crew-status-ring";
import { DashboardKpiGrid } from "@/features/dashboard/components/dashboard-kpi-grid";
import { PayPeriodProgress } from "@/features/dashboard/components/pay-period-progress";
import { SiteActivityStrip } from "@/features/dashboard/components/site-activity-strip";
import { SmartExceptions } from "@/features/dashboard/components/smart-exceptions";
import { CommandCenterCard } from "@/components/design";
import { PageHeader } from "@/components/design";
import { useDemo } from "../demo-context";
import { DEMO_COMPANY_NAME } from "../demo-data";

function mapPayPeriodStatus(
  status: string,
): "Draft" | "Submitted" | "Approved" | "Locked" | "Exported" | "Exception" {
  switch (status) {
    case "OPEN":
      return "Draft";
    case "IN_REVIEW":
      return "Submitted";
    case "APPROVED":
      return "Approved";
    case "LOCKED":
      return "Locked";
    case "EXPORTED":
      return "Exported";
    default:
      return "Draft";
  }
}

export function DemoDashboardPage() {
  const { state } = useDemo();
  const data = state.dashboard;

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Good morning, ${data.greeting.userName.split(" ")[0]}`}
        description={`${DEMO_COMPANY_NAME} · ${data.currentPayPeriod?.label ?? "Pay period"}`}
        badge={
          data.currentPayPeriod ? (
            <StatusBadge status={mapPayPeriodStatus(data.currentPayPeriod.status)} />
          ) : undefined
        }
      />

      <PayPeriodBanner payPeriod={data.currentPayPeriod} asOfDate={data.asOfDate} loading={false} />

      <DashboardKpiGrid kpis={data.kpis} currency={data.currency} loading={false} />

      <SiteActivityStrip sites={data.siteActivity} loading={false} />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <PayPeriodProgress
            steps={data.payPeriodProgress}
            payPeriod={data.currentPayPeriod}
            loading={false}
          />
        </div>
        <div className="rounded-xl border bg-card-white p-4 shadow-[var(--shadow-card)] text-sm text-muted-foreground">
          <p className="font-semibold text-navy-deep">Sample company</p>
          <p className="mt-2">
            Ridgepoint runs 3 active sites with 34 workers on the current biweekly period. This
            dashboard reflects real workflow states — submitted entries, pending approvals, and
            flagged exceptions.
          </p>
          <Link to="/demo/daily-entry" className="mt-3 inline-block font-medium text-orange-construction hover:underline">
            Open daily entry →
          </Link>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <CrewStatusRing crewStatus={data.crewStatus} loading={false} />
        <CommandCenterCard
          title="Smart exceptions"
          description="Issues flagged before payroll export"
          variant={data.kpis.exceptionsNeedingReview > 0 ? "alert" : "success"}
        >
          <SmartExceptions exceptions={data.smartExceptions} loading={false} />
        </CommandCenterCard>
      </div>
    </div>
  );
}
